import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getCourierPartnerOptions } from "../api/modules";

// Types for system configurations
export interface DispatchStatusConfig {
  value: string;
  label: string;
  icon?: string;
  color?: string;
  description?: string;
  nextStatuses?: string[];
}

export interface CourierPartnerConfig {
  id: string;
  value: string;
  label: string;
  name: string;
  code: string;
  email?: string;
  phone?: string;
  contactName?: string;
  apiUrl?: string;
  contact?: any;
  tracking: boolean;
  active: boolean;
  isActive: boolean;
}

export interface PaymentModeConfig {
  value: string;
  label: string;
  isActive: boolean;
}

export interface SystemConfigState {
  // Fixed business logic configurations
  dispatchStatuses: DispatchStatusConfig[];
  paymentModes: PaymentModeConfig[];

  // API-driven configurations
  courierPartners: CourierPartnerConfig[];

  // Utility functions
  getDispatchStatus: (value: string) => DispatchStatusConfig | undefined;
  getNextStatuses: (currentStatus: string) => DispatchStatusConfig[];
  getPaymentMode: (value: string) => PaymentModeConfig | undefined;
  findCourier: (id: string) => CourierPartnerConfig | null;
  getCourierDisplayName: (id: string) => string;

  // Generic functions for future dynamic configs
  getConfig: (configType: string, fallback?: any[]) => any[];
  loadConfig: (configType: string, bustCache?: boolean) => Promise<void>;
  refreshConfig: (configType: string) => Promise<void>;
  isConfigLoading: (configType: string) => boolean;
  getConfigError: (configType: string) => string | null;

  // Initialization
  initialized: boolean;
  refreshCouriers: () => Promise<void>;
}

const SystemConfigContext = createContext<SystemConfigState | undefined>(
  undefined
);

// FIXED CONFIGURATIONS (Business Logic - Not Configurable)
const DISPATCH_STATUSES: DispatchStatusConfig[] = [
  {
    value: "pending",
    label: "Pending",
    icon: "🕐",
    color:
      "bg-gray-500/20 text-gray-700 dark:text-gray-400 border border-gray-500/30",
    description: "Order received, waiting for confirmation",
    nextStatuses: ["confirmed", "cancelled"],
  },
  {
    value: "confirmed",
    label: "Confirmed",
    icon: "✓",
    color:
      "bg-blue-500/20 text-blue-700 dark:text-blue-400 border border-blue-500/30",
    description: "Order confirmed, preparing for packing",
    nextStatuses: ["packed", "cancelled"],
  },
  {
    value: "packed",
    label: "Packed",
    icon: "📦",
    color:
      "bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border border-yellow-500/30",
    description: "Items packed, ready for pickup by courier",
    nextStatuses: ["shipped", "cancelled"],
  },
  {
    value: "shipped",
    label: "Shipped",
    icon: "🚚",
    color:
      "bg-purple-500/20 text-purple-700 dark:text-purple-400 border border-purple-500/30",
    description: "Package picked up by courier",
    nextStatuses: ["in_transit", "failed"],
  },
  {
    value: "in_transit",
    label: "In Transit",
    icon: "🛣️",
    color: "bg-indigo-100 text-indigo-800",
    description: "Package is on the way to destination",
    nextStatuses: ["out_for_delivery", "failed"],
  },
  {
    value: "out_for_delivery",
    label: "Out for Delivery",
    icon: "🏃",
    color:
      "bg-orange-500/20 text-orange-700 dark:text-orange-400 border border-orange-500/30",
    description: "Package is out for final delivery",
    nextStatuses: ["delivered", "failed"],
  },
  {
    value: "delivered",
    label: "Delivered",
    icon: "✅",
    color:
      "bg-green-500/20 text-green-700 dark:text-green-400 border border-green-500/30",
    description: "Package successfully delivered to customer",
    nextStatuses: ["returned"],
  },
  {
    value: "failed",
    label: "Failed",
    icon: "❌",
    color:
      "bg-red-500/20 text-red-700 dark:text-red-400 border border-red-500/30",
    description: "Delivery attempt failed",
    nextStatuses: ["pending", "cancelled"],
  },
  {
    value: "returned",
    label: "Returned",
    icon: "↩️",
    color: "bg-red-50 text-red-700",
    description: "Package returned to sender",
    nextStatuses: ["pending"],
  },
  {
    value: "cancelled",
    label: "Cancelled",
    icon: "🚫",
    color:
      "bg-gray-500/20 text-gray-700 dark:text-gray-400 border border-gray-500/30",
    description: "Order cancelled",
    nextStatuses: [],
  },
];

const PAYMENT_MODES: PaymentModeConfig[] = [
  { value: "online", label: "Online Payment", isActive: true },
  { value: "offline", label: "Offline Payment", isActive: true },
  { value: "bank_transfer", label: "Bank Transfer", isActive: true },
  { value: "cheque", label: "Cheque", isActive: true },
  { value: "cash", label: "Cash", isActive: true },
  { value: "upi", label: "UPI", isActive: true },
  { value: "card", label: "Card Payment", isActive: true },
];

export const SystemConfigProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // API-driven state for configurable items
  const [courierPartners, setCourierPartners] = useState<
    CourierPartnerConfig[]
  >([]);
  const [initialized, setInitialized] = useState(false);
  const [configLoadingStates, setConfigLoadingStates] = useState<
    Record<string, boolean>
  >({});
  const [configErrors, setConfigErrors] = useState<
    Record<string, string | null>
  >({});

  // Fixed configuration getters
  const getDispatchStatus = (
    value: string
  ): DispatchStatusConfig | undefined => {
    return DISPATCH_STATUSES.find((status) => status.value === value);
  };

  const getNextStatuses = (currentStatus: string): DispatchStatusConfig[] => {
    const status = getDispatchStatus(currentStatus);
    if (!status?.nextStatuses) return [];

    return status.nextStatuses
      .map((statusValue) => getDispatchStatus(statusValue))
      .filter((status): status is DispatchStatusConfig => status !== undefined);
  };

  const getPaymentMode = (value: string): PaymentModeConfig | undefined => {
    return PAYMENT_MODES.find((mode) => mode.value === value);
  };

  const findCourier = (id: string): CourierPartnerConfig | null => {
    return courierPartners.find((courier) => courier.id === id) || null;
  };

  const getCourierDisplayName = (id: string): string => {
    const courier = findCourier(id);
    return courier ? courier.label : id;
  };

  // Generic configuration management
  const getConfig = (configType: string, fallback: any[] = []): any[] => {
    switch (configType) {
      case "courierPartners":
        return courierPartners;
      case "dispatchStatuses":
        return DISPATCH_STATUSES;
      case "paymentModes":
        return PAYMENT_MODES;
      default:
        return fallback;
    }
  };

  const setConfigLoading = (configType: string, loading: boolean) => {
    setConfigLoadingStates((prev) => ({ ...prev, [configType]: loading }));
  };

  const setConfigError = (configType: string, error: string | null) => {
    setConfigErrors((prev) => ({ ...prev, [configType]: error }));
  };

  const loadConfig = useCallback(
    async (configType: string, bustCache = false): Promise<void> => {
      try {
        setConfigLoading(configType, true);
        setConfigError(configType, null);

        switch (configType) {
          case "courierPartners": {
            const rawCouriers = await getCourierPartnerOptions(true, bustCache);

            // Ensure rawCouriers is an array before mapping
            const courierArray = Array.isArray(rawCouriers) ? rawCouriers : [];

            const couriers: CourierPartnerConfig[] = courierArray.map(
              (c: any) => ({
                id: String(c.id || c._id || c.value),
                value: String(c.value || c.code || c.id),
                label: String(c.label || c.name || c.value),
                name: String(c.name || c.label || c.value),
                code: String(c.code || c.value || c.id),
                email: c.email || undefined,
                phone: c.phone || undefined,
                contactName: c.contactName || undefined,
                apiUrl: c.apiUrl || undefined,
                contact: c.contact || undefined,
                tracking: Boolean(c.tracking),
                active: c.active !== false,
                isActive: c.active !== false,
              })
            );
            setCourierPartners(couriers);
            break;
          }
          default:
            console.warn(`Unknown config type: ${configType}`);
        }
      } catch (error) {
        console.error(`Error loading ${configType}:`, error);
        setConfigError(
          configType,
          error instanceof Error ? error.message : "Unknown error"
        );
      } finally {
        setConfigLoading(configType, false);
      }
    },
    []
  );

  const refreshConfig = useCallback(
    async (configType: string): Promise<void> => {
      await loadConfig(configType, true);
    },
    [loadConfig]
  );

  const isConfigLoading = (configType: string): boolean => {
    return configLoadingStates[configType] || false;
  };

  const getConfigError = (configType: string): string | null => {
    return configErrors[configType] || null;
  };

  const refreshCouriers = useCallback(async (): Promise<void> => {
    await loadConfig("courierPartners", true);
  }, [loadConfig]);

  // Initialize API-driven configurations
  useEffect(() => {
    const initializeConfigs = async () => {
      try {
        await Promise.all([loadConfig("courierPartners")]);
        setInitialized(true);
      } catch (error) {
        console.error("Failed to initialize system configs:", error);
      }
    };

    initializeConfigs();
  }, [loadConfig]);

  const value: SystemConfigState = {
    // Fixed configurations
    dispatchStatuses: DISPATCH_STATUSES,
    paymentModes: PAYMENT_MODES,

    // API-driven configurations
    courierPartners,

    // Utility functions
    getDispatchStatus,
    getNextStatuses,
    getPaymentMode,
    findCourier,
    getCourierDisplayName,

    // Generic functions
    getConfig,
    loadConfig,
    refreshConfig,
    isConfigLoading,
    getConfigError,

    // Initialization
    initialized,
    refreshCouriers,
  };

  return (
    <SystemConfigContext.Provider value={value}>
      {children}
    </SystemConfigContext.Provider>
  );
};

export const useSystemConfig = (): SystemConfigState => {
  const context = useContext(SystemConfigContext);
  if (!context) {
    throw new Error(
      "useSystemConfig must be used within a SystemConfigProvider"
    );
  }
  return context;
};
